import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, FilterIcon } from 'lucide-react';
import { UnifiedUpdateCard, UnifiedUpdatePost, UnifiedPostType } from './UnifiedUpdateCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useUserRole } from '../../context/UserRoleContext';
interface UpdatesFeedProps {
  posts: UnifiedUpdatePost[];
  title: string;
  preview?: boolean;
  maxPosts?: number;
  showFilters?: boolean;
  activeTypeFilter?: UnifiedPostType | 'All' | 'Industry';
  setActiveTypeFilter?: (filter: UnifiedPostType | 'All' | 'Industry') => void;
  activeServiceFilter?: string | 'All';
  setActiveServiceFilter?: (filter: string | 'All') => void;
  activeGenreFilter?: string | 'All';
  setActiveGenreFilter?: (filter: string | 'All') => void;
}
const READER_VISIBLE_TYPES: UnifiedPostType[] = ['New Release', 'Cover Reveal', 'Audiobook Launch', 'Sale', 'Event Announcement', 'Award Nomination'];
const SERVICE_CATEGORIES = ['Editing & Manuscript Development', 'Developmental Editing', 'Copy Editing & Proofreading', 'Sensitivity Reading', 'Beta Readers & Feedback', 'ARC Management & Reviews', 'Cover Design & Branding', 'Interior Formatting & Production', 'Audio & Narration', 'Marketing & Promotion', 'Publishing & Distribution', 'Creative & Specialty Services'];
const GENRES = ['Romance', 'Dark Romance', 'Fantasy', 'Science Fiction', 'Mystery', 'Thriller', 'Horror', 'Contemporary', 'Historical Fiction', 'Young Adult', 'New Adult', 'Literary Fiction', 'Cozy Mystery', 'Paranormal', 'Erotica'];
export const UpdatesFeed: React.FC<UpdatesFeedProps> = ({
  posts,
  title,
  preview = false,
  maxPosts = 5,
  showFilters = true,
  activeTypeFilter: controlledTypeFilter,
  setActiveTypeFilter: setControlledTypeFilter,
  activeServiceFilter: controlledServiceFilter,
  setActiveServiceFilter: setControlledServiceFilter,
  activeGenreFilter: controlledGenreFilter,
  setActiveGenreFilter: setControlledGenreFilter
}) => {
  const dk = useDarkClasses();
  const {
    isReader
  } = useUserRole();
  const [internalTypeFilter, setInternalTypeFilter] = useState<UnifiedPostType | 'All' | 'Industry'>('All');
  const [internalServiceFilter, setInternalServiceFilter] = useState<string | 'All'>('All');
  const [internalGenreFilter, setInternalGenreFilter] = useState<string | 'All'>('All');
  const activeTypeFilter = controlledTypeFilter ?? internalTypeFilter;
  const setActiveTypeFilter = setControlledTypeFilter ?? setInternalTypeFilter;
  const activeServiceFilter = controlledServiceFilter ?? internalServiceFilter;
  const setActiveServiceFilter = setControlledServiceFilter ?? setInternalServiceFilter;
  const activeGenreFilter = controlledGenreFilter ?? internalGenreFilter;
  const setActiveGenreFilter = setControlledGenreFilter ?? setInternalGenreFilter;
  const filteredPosts = useMemo(() => {
    let filtered = posts;
    if (isReader) {
      filtered = filtered.filter(post => READER_VISIBLE_TYPES.includes(post.type));
    }
    if (activeTypeFilter !== 'All') {
      if (activeTypeFilter === 'Industry') {
        const industryTypes: UnifiedPostType[] = ['Vendor Availability', 'Collaboration Request', 'Service Launch', 'Award Nomination', 'Event Announcement'];
        filtered = filtered.filter(post => industryTypes.includes(post.type));
      } else {
        filtered = filtered.filter(post => post.type === activeTypeFilter);
      }
    }
    if (!isReader && activeServiceFilter !== 'All') {
      filtered = filtered.filter(post => post.serviceCategory === activeServiceFilter);
    }
    // Genre filter applies to both roles
    if (activeGenreFilter !== 'All') {
      filtered = filtered.filter(post => post.genre === activeGenreFilter);
    }
    return filtered;
  }, [posts, isReader, activeTypeFilter, activeServiceFilter, activeGenreFilter]);
  const displayedPosts = preview ? filteredPosts.slice(0, maxPosts) : filteredPosts;
  const availableTypes = useMemo(() => {
    const types = new Set<UnifiedPostType>();
    const rolePosts = isReader ? posts.filter(p => READER_VISIBLE_TYPES.includes(p.type)) : posts;
    rolePosts.forEach(p => types.add(p.type));
    return Array.from(types);
  }, [posts, isReader]);
  const linkClass = isReader ? 'text-indigo-600 hover:text-indigo-700' : 'text-purple-600 hover:text-purple-700';
  const activeFilterClass = isReader ? dk.isDark ? 'bg-indigo-900/40 text-indigo-300 ring-1 ring-indigo-700' : 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200' : dk.isDark ? 'bg-purple-900/40 text-purple-300 ring-1 ring-purple-700' : 'bg-purple-100 text-purple-700 ring-1 ring-purple-200';
  const inactiveFilterClass = dk.isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200';
  return <section className={preview ? 'mb-10' : 'mb-0'} data-id="element-3641" data-mp="1e8kc2ik4qeei">
      <div className="flex items-center justify-between mb-6" data-id="element-3642" data-mp="1ea5523qsppa9">
        <h2 className={`text-xl sm:text-2xl font-bold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3643" data-mp="1s8v43ox6dz19">
          {title}
        </h2>
        {preview && <Link to="/trove/updates" className={`text-sm font-medium flex items-center ${linkClass}`} data-id="element-3644" data-mp="225g4fwy92lyr">
            View All Updates
            <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-3645" data-mp="1x527pb23myg1" />
          </Link>}
      </div>

      {showFilters && !controlledTypeFilter && <div className="mb-6 space-y-4" data-id="element-3646" data-mp="26xr6b5cjwhty">
          <div className="flex flex-wrap gap-2" data-id="element-3647" data-mp="26ysu8aeo0yt3">
            <button onClick={() => setActiveTypeFilter('All')} className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full transition-all ${activeTypeFilter === 'All' ? activeFilterClass : inactiveFilterClass}`} data-id="element-3648" data-mp="2kvpkoaqvge9g">
              All Updates
            </button>
            {!isReader && <button onClick={() => setActiveTypeFilter('Industry')} className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full transition-all ${activeTypeFilter === 'Industry' ? activeFilterClass : inactiveFilterClass}`} data-id="element-3649" data-mp="1ijb2vnfpxcg0">
                Industry Only
              </button>}
            {availableTypes.map(type => <button key={type} onClick={() => setActiveTypeFilter(type)} className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full transition-all ${activeTypeFilter === type ? activeFilterClass : inactiveFilterClass}`} data-id="element-3650" data-mp="217zm84yjz9po">
                {type}
              </button>)}
          </div>
          {!isReader && !preview && <div className={`p-4 rounded-lg ${dk.isDark ? 'bg-gray-800/50' : 'bg-gray-50'} border ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-3651" data-mp="fwhmbt3o4f6k">
              <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-3652" data-mp="3kfbbkuzpo9n">
                <FilterIcon className="h-3 w-3" data-id="element-3653" data-mp="3uq6zwft4csuc" />
                Filter by Service Category
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-3654" data-mp="3qov8q5uljzk1">
                <button onClick={() => setActiveServiceFilter('All')} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${activeServiceFilter === 'All' ? dk.isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700' : dk.isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} data-id="element-3655" data-mp="ra9ns1bedatd">
                  All Services
                </button>
                {SERVICE_CATEGORIES.map(category => <button key={category} onClick={() => setActiveServiceFilter(category)} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${activeServiceFilter === category ? dk.isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700' : dk.isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} data-id="element-3656" data-mp="11z4bnqqcrzad">
                    {category}
                  </button>)}
              </div>
            </div>}
          {!preview && <div className={`p-4 rounded-lg ${dk.isDark ? 'bg-gray-800/50' : 'bg-gray-50'} border ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-3657" data-mp="1ayremgyqdd5j">
              <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-3658" data-mp="23nydxi60e6ru">
                <FilterIcon className="h-3 w-3" data-id="element-3659" data-mp="28gj2o5a25toi" />
                Filter by Genre
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-3660" data-mp="3t3skb066jjxm">
                <button onClick={() => setActiveGenreFilter('All')} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${activeGenreFilter === 'All' ? isReader ? dk.isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700' : dk.isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700' : dk.isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} data-id="element-3661" data-mp="3o6scoktom94e">
                  All Genres
                </button>
                {GENRES.map(genre => <button key={genre} onClick={() => setActiveGenreFilter(genre)} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${activeGenreFilter === genre ? isReader ? dk.isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700' : dk.isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700' : dk.isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} data-id="element-3662" data-mp="3e7ba4c9q8bcn">
                    {genre}
                  </button>)}
              </div>
            </div>}
        </div>}

      <div className="space-y-4" data-id="element-3663" data-mp="7924q10p13ns">
        {displayedPosts.length > 0 ? displayedPosts.map(post => <UnifiedUpdateCard key={post.id} post={post} data-id="element-3664" data-mp="2b0nzmqqpgfh" />) : <div className={`text-center py-12 rounded-lg border border-dashed ${dk.isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-300 bg-gray-50'}`} data-id="element-3665" data-mp="3tf8tkjbtvvup">
            <p className={`text-sm ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3666" data-mp="1rn2fkmnfimdc">
              No updates found matching your criteria.
            </p>
            <button onClick={() => {
          setActiveTypeFilter('All');
          setActiveServiceFilter('All');
          setActiveGenreFilter('All');
        }} className={`mt-2 text-sm font-medium ${isReader ? 'text-indigo-600' : 'text-purple-600'} hover:underline`} data-id="element-3667" data-mp="35iknyi5hfo1p">
              Clear filters
            </button>
          </div>}
      </div>
    </section>;
};